import { useState } from 'react';

import { EllipsisVerticalIcon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import type { ApiError } from '@/api/fetcher';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { getPermissions } from '@/redux/permissions/selectors';
import type { Contact, ContactDebt } from '@/shared/types/contacts';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import { useContactDebts } from '../hooks/useContactDebts';
import { useContacts } from '../hooks/useContacts';

interface ContactActionsCellProps {
  contact: Contact;
}

const DEBT_COMPLIANCE_FLAGS = [
  'cease_desist',
  'debt_disputed',
  'coerced_debt',
  'refusal_to_pay',
  'fraud_or_identity_theft',
] as const satisfies readonly (keyof ContactDebt)[];

const hasComplianceFlag = (debt: ContactDebt) => DEBT_COMPLIANCE_FLAGS.some((flag) => debt[flag]);

export const ContactActionsCell = ({ contact }: ContactActionsCellProps) => {
  const { t } = useTranslation();
  const { deleteContact, isDeleting } = useContacts();
  const canDelete = useSelector((state: RootState) =>
    getPermissions(state, 'contacts', actions.DELETE)
  );
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [force, setForce] = useState(false);
  const [hasConflict, setHasConflict] = useState(false);
  const { debts, status: debtsStatus } = useContactDebts(
    isDeleteOpen ? contact.contact_id : undefined
  );
  const flaggedCount = debts.filter(hasComplianceFlag).length;
  const showForce = flaggedCount > 0 || hasConflict;

  const handleOpenChange = (open: boolean) => {
    setIsDeleteOpen(open);
    if (!open) {
      setForce(false);
      setHasConflict(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteContact({ contactId: contact.contact_id, force });
      toast.success(t('contacts.deleted'));
      handleOpenChange(false);
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError.statusCode === 409 && apiError.code === 'contact-has-flagged-debts') {
        setHasConflict(true);
        return;
      }
      const message = error instanceof Error ? error.message : t('contacts.delete_failed');
      toast.error(message);
    }
  };

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            variant="ghost-neutral"
            size="icon-sm"
            disabled={!canDelete}
            aria-label={t('common.more')}
          >
            <EllipsisVerticalIcon size={16} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem
            variant="destructive"
            className="flex items-center gap-2"
            disabled={!canDelete}
            onSelect={() => setIsDeleteOpen(true)}
          >
            <Trash2Icon size={16} />
            {t('common.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={isDeleteOpen} onOpenChange={handleOpenChange}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('contacts.delete_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('contacts.delete_description', { 0: contact.name || t('contacts.unnamed') })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {debtsStatus === 'pending' && <Skeleton className="h-4 w-48" />}
          {debts.length > 0 && (
            <p className="text-sm text-foreground-muted">
              {t('contacts.delete_linked_debts', { count: debts.length })}
            </p>
          )}
          {flaggedCount > 0 && !hasConflict && (
            <p className="text-sm text-warning">
              {t('contacts.delete_flagged_debts', { count: flaggedCount })}
            </p>
          )}
          {showForce && (
            <div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="forceDeleteDebts"
                  checked={force}
                  onCheckedChange={(checked) => setForce(checked as boolean)}
                />
                <label
                  htmlFor="forceDeleteDebts"
                  className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {t('contacts.delete_force_debts')}
                </label>
              </div>
              <div className="mt-1 ml-6 text-xs text-foreground-muted">
                {t('contacts.delete_force_debts_help')}
              </div>
              {hasConflict && (
                <p role="alert" className="mt-2 text-sm text-destructive">
                  {t('contacts.delete_flagged_debts_conflict')}
                </p>
              )}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <Button variant="destructive" isLoading={isDeleting} onClick={handleConfirmDelete}>
              {t('common.delete')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
